// Plan & billing client of the site (server/API.md «Billing», ADR-0045/0046): the state, the actions of Account → Plan,
// the invoices and the Stripe customer portal. The state is our row, changed only by Stripe webhooks - an action answers
// `{ ok, pending: true }` and the page polls GET /api/billing/state until the change lands.
import { useCallback, useEffect, useState } from "react";
import { ApiError, call, getBillingState, type BillingPeriod, type BillingPlan, type BillingState, type PlanStateName } from "./api";
import { t, type MessageKey } from "./i18n";

export interface Invoice {
  number: string | null;
  date: string;
  amount: number;
  currency: string;
  status: string;
  hosted_invoice_url: string | null;
  invoice_pdf: string | null;
  receipt_url: string | null;
}

export const getInvoices = () => call<Invoice[]>("/api/billing/invoices", { redirectOn401: false });
/** A Customer Portal session (card, invoices, billing details): the browser goes to `url`. */
export type PortalFlow = "payment_method" | "subscription_update";
export const portalSession = (flow?: PortalFlow) =>
  call<{ url: string }>("/api/billing/portal", { method: "POST", ...(flow ? { body: { flow } } : {}) });
export const cancelPlan = () => call<{ ok: true; pending: true }>("/api/billing/cancel", { method: "POST" });
export const resumePlan = () => call<{ ok: true; pending: true }>("/api/billing/resume", { method: "POST" });
export const switchPlan = (period: BillingPeriod) =>
  call<{ ok: true; pending: true }>("/api/billing/switch", { method: "POST", body: { period } });
/** The webhook did not land: the server reads the Checkout session itself; answers like GET /api/billing/state. */
export const syncCheckout = (sessionId: string) =>
  call<BillingState>(`/api/billing/checkout/sync?session_id=${encodeURIComponent(sessionId)}`, { redirectOn401: false });

/** ADR-0059: redeem a gift code (a site action only); the answer carries the new state like GET /api/billing/state. */
export const redeemGiftCode = (code: string) =>
  call<BillingState & { ok: true; weeks: number }>("/api/billing/redeem", { method: "POST", body: { code }, redirectOn401: false });

/** The Home strip shows only these (BILLING-spec, ADR-0045 «Site» column). */
export const STRIP_STATES: PlanStateName[] = ["trial-ending", "expired", "past-due", "paused"];
/** …and a gift in its last 10 days (BL-185; the state stays `gift`, the server sets `gift_ending`). */
export const showsStrip = (plan: BillingPlan): boolean => STRIP_STATES.includes(plan.state) || (plan.state === "gift" && plan.gift_ending === true);

/** Polling after an action or the Checkout return (ADR-0046: a few seconds until the webhook lands). */
export const POLL_MS = 2000;
export const ACTION_WAIT_MS = 16_000;
export const CHECKOUT_SYNC_AFTER_MS = 10_000;
export const CHECKOUT_WAIT_MS = 30_000;

export const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * GET /api/billing/state of the signed-in user: undefined - loading, null - the server could not tell (nothing is
 * shown then). `reload` fetches again and returns the new state; `set` takes one from a sync answer. `auto: false` -
 * the caller does the first fetch itself (Account polls after the Checkout return).
 */
export function useBillingState(signedIn: boolean, auto = true) {
  const [state, set] = useState<BillingState | null | undefined>(undefined);
  const reload = useCallback(async () => {
    try {
      const s = await getBillingState();
      const next = s?.plan ? s : null;
      set(next);
      return next;
    } catch {
      set(null);
      return null;
    }
  }, []);
  useEffect(() => {
    if (signedIn && auto) void reload();
  }, [signedIn, auto, reload]);
  return { state, reload, set };
}

/** "€19.99", "€199" - the plan prices as written in the mockups. */
export function formatPrice(amount: number, currency = "EUR"): string {
  const symbol = currency.toUpperCase() === "EUR" ? "€" : `${currency.toUpperCase()} `;
  return `${symbol}${Number.isInteger(amount) ? amount : amount.toFixed(2)}`;
}

/** An invoice amount with its cents: "€19.99", "€199.00". */
export function formatAmount(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat("en-IE", { style: "currency", currency: currency.toUpperCase() }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

export const planPrices = (plan: BillingPlan) => ({
  monthly: formatPrice(plan.price?.monthly ?? 19.99, plan.price?.currency),
  yearly: formatPrice(plan.price?.yearly ?? 199, plan.price?.currency),
});

/** "1 day", "12 days". */
export const daysText = (n: number) => (n === 1 ? t("plan.day") : t("plan.days", { n }));

/** A subscription that ran out (cancelled and the period is over) is `expired` with a period end; a trial has none. */
export const subscriptionEnded = (plan: BillingPlan) => plan.state === "expired" && Boolean(plan.period_end);

/** One sentence for a refused billing call. */
export function billingErrorText(e: unknown): string {
  const code = e instanceof ApiError ? e.code : "";
  const keys: Record<string, MessageKey> = {
    no_subscription: "plan.err.noSubscription",
    already_cancelled: "plan.err.alreadyCancelled",
    not_cancelled: "plan.err.notCancelled",
    same_period: "plan.err.samePeriod",
    cannot_switch: "plan.err.cannotSwitch",
    no_billing_account: "plan.err.noBillingAccount",
    billing_error: "plan.err.unavailable",
  };
  return t(keys[code] ?? "plan.err.failed");
}

/** «Update card», «Edit in Stripe»: a portal session, then the browser goes there. */
export function usePortal() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const open = useCallback(async (flow?: PortalFlow) => {
    setBusy(true);
    setError(null);
    try {
      const { url } = await portalSession(flow);
      window.location.assign(url);
    } catch (e) {
      setBusy(false);
      setError(billingErrorText(e));
    }
  }, []);
  return { open, busy, error };
}

/** One sentence for a refused redemption: a wrong, used, expired or revoked code is one and the same line (ADR-0059). */
export function redeemErrorText(e: unknown): string {
  const code = e instanceof ApiError ? e.code : "";
  const keys: Record<string, MessageKey> = {
    invalid_code: "plan.gift.err.invalid",
    gift_active: "plan.gift.err.giftActive",
    has_subscription: "plan.gift.err.hasSubscription",
    complimentary: "plan.gift.err.complimentary",
    billing_off: "plan.gift.err.billingOff",
    too_many_attempts: "plan.gift.err.tooMany",
  };
  return t(keys[code] ?? "plan.gift.err.failed");
}
